/**
 * useAttribution
 *
 * Reads the `metwide_attr` first-party cookie populated by the
 * `track-attribution.client.ts` plugin and returns the captured
 * Google Ads click-IDs + UTM tags as a flat object suitable for
 * spreading into a lead-form `$fetch` body.
 *
 * Keys without a value are omitted, so a clean cookie produces an
 * empty object and no `null` overrides reach the api.
 */
import {
  ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
  ATTRIBUTION_COOKIE_NAME,
  attributionMaxLength,
  LEAD_FORWARDED_KEYS,
  type LeadAttributionPayload,
} from '~~/shared/lead-attribution'

const COOKIE_OPTIONS = {
  maxAge: ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
  path: '/',
  sameSite: 'lax' as const,
  secure: true,
}

/**
 * Returns the underlying `useCookie` ref so the plugin can write to
 * it. Components should use `useAttribution()` instead.
 */
export function useAttributionCookie() {
  return useCookie<LeadAttributionPayload | null>(
    ATTRIBUTION_COOKIE_NAME,
    COOKIE_OPTIONS,
  )
}

/**
 * Read attribution at form-submit time. Always safe to spread into
 * a request body; produces `{}` when the cookie is missing or empty.
 */
export function useAttribution(): LeadAttributionPayload {
  const cookie = useAttributionCookie()
  const stored = cookie.value
  if (!stored || typeof stored !== 'object') return {}
  const out: LeadAttributionPayload = {}
  for (const key of LEAD_FORWARDED_KEYS) {
    const value = stored[key]
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed === '') continue
    out[key] = trimmed.slice(0, attributionMaxLength(key))
  }
  return out
}
